/**
 * AddInterventionDialog — record a practice-metrics intervention marker for an org.
 *
 * Internal admin affordance for the "we tweaked a template/rule, mark it so the
 * before/after line shows on the tile" flow. Writes via the org-scoped admin route
 * (POST /v2/admin/semantic/organizations/{org}/interventions). A metric_slug scopes
 * the marker to one tile; omit it for an org-wide marker.
 *
 * On success it invalidates the affected tile's shared server-ui cache entry so the
 * dashed marker line appears immediately on the grid tile and the drill-in loop card.
 */
import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { serverUiApi } from '@features/server-ui/store/api';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';

import { useAdminRecordInterventionMutation } from '../store/hooks';

const KIND_OPTIONS = [
  { value: 'template_change', label: 'Template change' },
  { value: 'nudge', label: 'Nudge' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' },
];

const componentEndpoint = (organizationId: string, slug: string) =>
  `/v2/admin/semantic/organizations/${organizationId}/analytics/components/${slug}`;

interface AddInterventionDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  /** Scopes the marker to one metric; omit for an org-wide marker. */
  metricSlug?: string;
  metricName?: string;
  onSuccess?: (metricSlug?: string) => void;
}

export const AddInterventionDialog: React.FC<AddInterventionDialogProps> = ({
  open,
  onClose,
  organizationId,
  metricSlug,
  metricName,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [recordIntervention, { isLoading }] = useAdminRecordInterventionMutation();

  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('template_change');
  const [occurredAt, setOccurredAt] = useState('');
  const [link, setLink] = useState('');

  useEffect(() => {
    if (open) {
      setLabel('');
      setKind('template_change');
      setOccurredAt('');
      setLink('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!label.trim()) {
      enqueueSnackbar('Describe what changed', { variant: 'warning' });
      return;
    }
    try {
      await recordIntervention({
        organizationId,
        recordInterventionRequest: {
          label: label.trim(),
          kind,
          metric_slug: metricSlug ?? null,
          occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
          link: link.trim() || null,
        },
      }).unwrap();

      enqueueSnackbar('Intervention marker added', { variant: 'success' });

      // Refresh the affected tile(s): a metric-scoped marker refreshes that tile's
      // shared endpoint cache; an org-wide marker refreshes every tile.
      if (metricSlug) {
        dispatch(
          serverUiApi.util.invalidateTags([
            {
              type: 'ServerUiComponent' as const,
              id: componentEndpoint(organizationId, metricSlug),
            },
          ]),
        );
      } else {
        dispatch(serverUiApi.util.invalidateTags(['ServerUiComponent']));
      }

      onSuccess?.(metricSlug);
      onClose();
    } catch (err) {
      const e = err as { data?: { message?: string; detail?: string } };
      enqueueSnackbar(
        e?.data?.message || e?.data?.detail || 'Failed to add intervention',
        { variant: 'error' },
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Add intervention marker
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              fontSize: 13,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Applies to
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {metricSlug
                ? metricName ?? metricSlug
                : 'Org-wide — annotates the whole practice'}
            </Typography>
          </Box>

          <TextField
            label="What changed"
            placeholder="e.g. Added carry-forward med-list block to the MM template"
            value={label}
            onChange={e => setLabel(e.target.value)}
            fullWidth
            required
            autoFocus
            multiline
            minRows={2}
          />
          <TextField
            select
            label="Kind"
            value={kind}
            onChange={e => setKind(e.target.value)}
            fullWidth
          >
            {KIND_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="When it went live"
            type="date"
            value={occurredAt}
            onChange={e => setOccurredAt(e.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Defaults to now if left blank. This is where the marker line lands."
          />
          <TextField
            label="Link (optional)"
            placeholder="Deep-link to the template diff or settings change"
            value={link}
            onChange={e => setLink(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Add marker
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInterventionDialog;
